// Cloche de notifications dans le SideRail vertical gauche. Porte deux flux :
//   1. Invitations en attente pour l'email du user (cross-org) → « Accepter »
//      in-app (cf useAcceptInvitation).
//   2. Demandes d'accès aux apps dont le user est propriétaire (table
//      `appAccess`) → « Approuver / Refuser » (decideRequest).
// Affiche un badge avec le total, et un popover (ouvert vers la DROITE, le rail
// étant collé au bord gauche) avec une section par flux.
//
// Ne rend RIEN tant qu'il n'y a rien à traiter — pas de cloche vide qui
// encombre le rail. Les deux sources (listMyPending / pendingForOwner) sont
// réactives : la cloche disparaît dès que tout est traité.
//
// Partage useAcceptInvitation avec PendingInvitationsBanner : même action
// serveur, même état "en cours", même gestion d'erreur.

import { useMutation, useQuery } from "convex/react";
import { Bell } from "@/shared/ui/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Etiquette, Infobulle, Ligne, Lignes, Volet, useVolet } from "@/shared/ui";
import { useAcceptInvitation } from "./useAcceptInvitation";

/** Libellé d'app pour le sous-titre d'une demande. V1 : Twitter built-in. */
function appLabel(appId: string): string {
  return appId === "twitter" ? "Twitter" : appId;
}

export function NotificationsTray() {
  const { t } = useTranslation();
  const pendings = useQuery(api.invitations.listMyPending, {});
  // Demandes d'accès en attente sur les apps dont je suis propriétaire ([] pour
  // les non-propriétaires). Owner-only côté serveur, donc gratuit ici.
  const accessRequests = useQuery(api.appAccess.pendingForOwner, {});
  const decideRequest = useMutation(api.appAccess.decideRequest);
  const volet = useVolet();
  const { fermer } = volet;
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const { acceptInvitation, acceptingId } = useAcceptInvitation();

  const invites = pendings ?? [];
  const requests = accessRequests ?? [];
  const count = invites.length + requests.length;

  async function decide(rowId: Id<"appAccess">, approve: boolean) {
    setDecidingId(rowId);
    try {
      await decideRequest({ rowId, approve });
    } finally {
      setDecidingId(null);
    }
  }

  // Dernière invitation acceptée → plus rien à montrer, on referme.
  useEffect(() => {
    if (count === 0) fermer();
  }, [count, fermer]);

  // Rien à montrer (ni invitation, ni demande d'accès) = pas de cloche.
  if (count === 0) return null;

  return (
    <>
      <Infobulle texte={t("shell.notifications.title")} cote="droite">
        <Button
          ref={volet.ancre}
          variant={volet.ouvert ? "secondary" : "ghost"}
          size="lg"
          iconOnly
          className="relative h-9 w-9"
          onClick={volet.basculer}
          aria-haspopup="dialog"
          aria-expanded={volet.ouvert}
          aria-label={t("shell.notifications.title")}
        >
          <Bell className="h-[18px] w-[18px]" />
          <Badge ton="marque" className="absolute -right-1.5 -top-1.5 h-4 min-w-4 justify-center px-1">
            {count}
          </Badge>
        </Button>
      </Infobulle>

      {/* Le `Volet` de la librairie : porté sur <body>, sous la cloche (au-dessus si la place
          manque — la cloche est en bas du rail), Échap et un clic dehors le ferment. */}
      <Volet
        ancre={volet.ancre}
        ouvert={volet.ouvert}
        onClose={volet.fermer}
        ariaLabel={t("shell.notifications.title")}
        largeur={320}
        remplissage="aucun"
      >
        <Etiquette as="h3" className="px-4 pb-1 pt-3">
          {t("shell.notifications.title")}
        </Etiquette>
        <div className="max-h-72 overflow-y-auto pb-2">
          {/* Demandes d'accès aux apps (owner) — Approuver / Refuser. */}
          {requests.length > 0 && (
            <section className="px-2 py-1">
              <Etiquette as="h3" className="px-2 py-1">
                {t("shell.notifications.accessRequests")}
              </Etiquette>
              <Lignes>
                {requests.map((r) => {
                  const deciding = decidingId === r._id;
                  return (
                    <Ligne
                      key={r._id}
                      etiquette={r.requester.name ?? r.requester.email ?? "—"}
                      sous={t("shell.notifications.wantsAccess", { app: appLabel(r.appId) })}
                      apres={
                        <span className="flex shrink-0 items-center gap-1">
                          <Button variant="primary" size="sm" disabled={deciding} onClick={() => void decide(r._id, true)}>
                            {t("shell.notifications.approve")}
                          </Button>
                          <Button variant="secondary" size="sm" disabled={deciding} onClick={() => void decide(r._id, false)}>
                            {t("shell.notifications.deny")}
                          </Button>
                        </span>
                      }
                    />
                  );
                })}
              </Lignes>
            </section>
          )}

          {/* Invitations en attente (inchangé). En-tête de section seulement
              s'il y a aussi des demandes d'accès, pour ne pas alourdir le cas
              « invitations seules ». */}
          {invites.length > 0 && (
            <section className="px-2 py-1">
              {requests.length > 0 && (
                <Etiquette as="h3" className="px-2 py-1">
                  {t("shell.notifications.invitations")}
                </Etiquette>
              )}
              <Lignes>
                {invites.map((p) => (
                  <Ligne
                    key={p._id}
                    etiquette={p.accountName ?? "—"}
                    sous={p.roleName || undefined}
                    apres={
                      <Button variant="accent" size="sm" onClick={() => void acceptInvitation(p._id, p.accountName)} disabled={acceptingId !== null}>
                        {acceptingId === p._id ? t("invitations.banner.accepting") : t("invitations.banner.accept")}
                      </Button>
                    }
                  />
                ))}
              </Lignes>
            </section>
          )}
        </div>
      </Volet>
    </>
  );
}
